// What this browser remembers of the signed-in account's period of Remove Ads, so the ads stay away before Firebase
// loads (docs/decisions.md, "Remove Ads — 364 days"). It is only a presentation shortcut, never the authority: the
// server's records decide, the entitlement subscription (entitlements.ts) checks them on every page of a remembered
// buyer, and anyone can edit localStorage. The record is JSON:
//
//   { v: 2, uid, since, until, verifiedAt, offset }            or, set by this device's clock alone,
//   { v: 2, uid, since, until, verifiedAt, offset: 0, clock: "device" }
//
// `since` and `until` are the period (milliseconds, `until` exclusive), `verifiedAt` when the server last confirmed it,
// all by the server's clock; `offset` is the server's clock minus this device's at that check. `clock: "device"` marks a
// record written when the server's time couldn't be had (entitlements.ts): its times are the device's, which may be
// off, so it never stands in for the server's clock and never outranks a record set by it. It counts while the
// server's clock as last seen (Date.now() + offset) is within the period, within AD_FREE_VERIFIED_FOR_MS of the last
// check (offline, the ads come back after that until the server answers again; never after the period's end), and not
// earlier than the last check (a clock set back makes it count for nothing). Anything else (the old "1", a malformed or
// ended record) counts for nothing and makes the page check again. The inline <head> script (build/render.ts,
// `headScript`) applies the same test before first paint. Every localStorage access is guarded: it throws in some
// private modes and when storage is blocked.

export const AD_FREE_STORAGE_KEY = 'chordToMidi.adFree';
export const AD_FREE_CACHE_VERSION = 2;
/** How long a check by the server counts without another one: a week. */
export const AD_FREE_VERIFIED_FOR_MS = 7 * 86_400_000;
/** How far the clock may seem to go back before the last check (clock adjustments) before the record stops counting. */
export const AD_FREE_CLOCK_SLACK_MS = 5 * 60_000;
/** The longest single wait of a timer (browsers run longer ones at once): the deadline is checked again after it. */
const MAX_TIMER_MS = 6 * 3_600_000;

export interface AdFreeRecord {
  v: typeof AD_FREE_CACHE_VERSION;
  uid: string;
  since: number;
  until: number;
  verifiedAt: number;
  offset: number;
  /** "device": set by this device's clock alone (offset 0), not the server's. Absent: by the server's clock. */
  clock?: 'device';
}

type AdFreeListener = (adFree: boolean) => void;

const listeners = new Set<AdFreeListener>();
/** Serves this page when localStorage can't be used. */
let memory: { raw: string | null } = { raw: null };
let watching = false;
let timer: ReturnType<typeof setTimeout> | undefined;
/** What the listeners were last told (or would have been): whether the record counted then. */
let lastAnnounced: boolean | null = null;
/** Counts the times this page forgot the record (sign-out, another account): answers begun before then are stale. */
let generation = 0;

function readRaw(): string | null {
  try {
    return localStorage.getItem(AD_FREE_STORAGE_KEY);
  } catch {
    return memory.raw;
  }
}

function writeRaw(raw: string | null): void {
  memory = { raw };
  try {
    if (raw === null) localStorage.removeItem(AD_FREE_STORAGE_KEY);
    else localStorage.setItem(AD_FREE_STORAGE_KEY, raw);
  } catch {
    // Storage unavailable: `memory` answers on this page.
  }
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** The stored record, if it has the current shape (whether or not it counts now). */
export function storedAdFree(raw: string | null = readRaw()): AdFreeRecord | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<AdFreeRecord> | null;
    if (
      !value ||
      typeof value !== 'object' ||
      value.v !== AD_FREE_CACHE_VERSION ||
      typeof value.uid !== 'string' ||
      value.uid === '' ||
      !finite(value.since) ||
      !finite(value.until) ||
      !finite(value.verifiedAt) ||
      !finite(value.offset) ||
      value.since >= value.until
    ) {
      return null;
    }
    return {
      v: AD_FREE_CACHE_VERSION,
      uid: value.uid,
      since: value.since,
      until: value.until,
      verifiedAt: value.verifiedAt,
      offset: value.offset,
      ...(value.clock === 'device' ? { clock: 'device' as const } : {}),
    };
  } catch {
    return null;
  }
}

/** The record counts at this device time: the head script's test exactly. */
export function recordCounts(record: AdFreeRecord, deviceNow: number = Date.now()): boolean {
  const now = deviceNow + record.offset;
  return (
    record.since <= now &&
    now < record.until &&
    now >= record.verifiedAt - AD_FREE_CLOCK_SLACK_MS &&
    now < record.verifiedAt + AD_FREE_VERIFIED_FOR_MS
  );
}

/**
 * What the browser remembers: `running` (it counts now), `stale` (something is stored that doesn't count: the old "1",
 * a malformed, ended or long-unchecked record, or one that hasn't started), or `none`. Anything but `none` is checked
 * with the server when the page can.
 */
export function adFreeCacheState(deviceNow: number = Date.now()): 'running' | 'stale' | 'none' {
  const raw = readRaw();
  if (raw === null) return 'none';
  const record = storedAdFree(raw);
  return record && recordCounts(record, deviceNow) ? 'running' : 'stale';
}

/** Synchronous, for the ad decision: true only while a remembered period counts (adFreeCacheState 'running'). */
export function cachedAdFree(deviceNow: number = Date.now()): boolean {
  return adFreeCacheState(deviceNow) === 'running';
}

/** The account the record belongs to, if any. */
export function cachedAdFreeUid(): string | null {
  return storedAdFree()?.uid ?? null;
}

/**
 * Records the account's period as the server confirmed it (null: none, or signed out), and tells the listeners if
 * whether it counts changed. A period that has ended isn't kept.
 */
export function setCachedAdFree(record: AdFreeRecord | null): void {
  const keep = record !== null && record.until > Date.now() + record.offset ? record : null;
  writeRaw(keep ? JSON.stringify(keep) : null);
  reconsider();
}

/** Forgets what is stored (sign-out, another account), telling the listeners if that changes anything. */
export function clearCachedAdFree(): void {
  generation += 1;
  setCachedAdFree(null);
}

/**
 * Changes whenever this page forgets the record (clearCachedAdFree: a sign-out, another account). An answer begun
 * before the change belongs to a session that has ended and must not be recorded (purchase.ts, confirmPayment).
 */
export function adFreeCacheGeneration(): number {
  return generation;
}

/**
 * Calls `listener` whenever whether a remembered period counts changes: a purchase confirmed, the period read from
 * Firestore, refunded, moved or ended (also while the page stays open), a sign-out, or the same in another tab. Not
 * called with the current value; read cachedAdFree() for that.
 */
export function onAdFreeChanged(listener: AdFreeListener): () => void {
  listeners.add(listener);
  watch();
  if (lastAnnounced === null) lastAnnounced = cachedAdFree();
  schedule();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) schedule();
  };
}

function notify(adFree: boolean): void {
  for (const listener of [...listeners]) {
    try {
      listener(adFree);
    } catch (error) {
      console.error(error);
    }
  }
}

/** Tells the listeners if whether the record counts changed since they were last told, and sets the next check. */
function reconsider(): void {
  const now = cachedAdFree();
  const changed = lastAnnounced !== null && now !== lastAnnounced;
  lastAnnounced = now;
  if (changed) notify(now);
  schedule();
}

/**
 * The device time at which the stored record next changes whether it counts: its start, its end or the end of its last
 * check; null when nothing stored will change by itself.
 */
export function nextAdFreeChange(deviceNow: number = Date.now()): number | null {
  const record = storedAdFree();
  if (!record) return null;
  const boundaries = [record.since, record.until, record.verifiedAt + AD_FREE_VERIFIED_FOR_MS].map(
    (serverTime) => serverTime - record.offset,
  );
  const ahead = boundaries.filter((at) => at > deviceNow);
  return ahead.length > 0 ? Math.min(...ahead) : null;
}

function schedule(): void {
  if (typeof window === 'undefined') return;
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  if (listeners.size === 0) return;
  const at = nextAdFreeChange();
  if (at === null) return;
  // Long waits in steps: a timer of more than about 24.8 days fires at once, and a sleeping device delays any.
  const wait = Math.min(Math.max(at - Date.now(), 0), MAX_TIMER_MS);
  timer = setTimeout(() => {
    timer = undefined;
    const record = storedAdFree();
    // An ended period is dropped: it can never count again.
    if (record && Date.now() + record.offset >= record.until) writeRaw(null);
    reconsider();
  }, wait + 1);
}

function watch(): void {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  // Another tab: a purchase, a refund, a sign-out there.
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key === AD_FREE_STORAGE_KEY) reconsider();
  });
  // Back after a while (a sleeping laptop, another tab): the timers may be late.
  const again = () => {
    if (document.visibilityState !== 'hidden') reconsider();
  };
  window.addEventListener('focus', again);
  window.addEventListener('pageshow', again);
  document.addEventListener('visibilitychange', again);
}

/** For tests: forget the listeners, the timer and the page's memory. */
export function resetAdFreeCacheForTests(): void {
  listeners.clear();
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  memory = { raw: null };
  lastAnnounced = null;
}
